import { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Popover, Typography } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { getInspectionChecks } from '@/actions/inspectionCheck';
import LoadingMask from '@/components/shared/LoadingMask';
import { useApi } from '@/providers/ApiContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import type { Inspection } from '@/types/apiTypes';
import type { EntityId } from '@/types/apiTypes';

const POLL_INTERVAL = 5000; // 5 sec
const PAGE_SIZE = 20;

interface InspectionCheckResult {
  entity_id: EntityId;
  check_section?: string;
  check_name: string;
  status?: string;
  compliant?: boolean | null;
  rationale?: string | null;
  suggestion?: string | null;
  [key: string]: unknown;
}

interface RunningInspectionDocumentChecksProps {
  inspection: Inspection & {
    entity_id: EntityId;
    [key: string]: unknown;
  };
  document?: {
    document_id?: string;
    [key: string]: unknown;
  } | null;
}

function RunningInspectionDocumentChecks({
  inspection,
  document,
}: RunningInspectionDocumentChecksProps): React.JSX.Element {
  const api = useApi();
  const [checks, setChecks] = useState<InspectionCheckResult[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [cursors, setCursors] = useState<Record<number, string>>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState<string>('');

  const fetchChecks = useCallback(
    async (page: number, startKey: string | null = null) => {
      if (!api) return;
      try {
        const response = await getInspectionChecks(
          api,
          inspection.entity_id,
          document?.document_id,
          undefined,
          PAGE_SIZE,
          startKey || undefined,
        );

        const sortedResults = (response.results || []).sort((a, b) => {
          const aSection = a.check_section || '';
          const bSection = b.check_section || '';
          if (aSection === bSection) {
            const aName = a.check_name || '';
            const bName = b.check_name || '';
            return aName > bName ? 1 : aName < bName ? -1 : 0;
          } else {
            if (aSection === 'global') {
              return -1;
            } else if (bSection === 'global') {
              return 1;
            } else {
              return aSection > bSection ? 1 : aSection < bSection ? -1 : 0;
            }
          }
        });

        setChecks(sortedResults as unknown as InspectionCheckResult[]);
        setHasNextPage(response.last_key !== null);

        // Store the cursor for the next page
        const lastKey = response.last_key;
        if (lastKey) {
          setCursors((prev) => ({
            ...prev,
            [page + 1]: lastKey,
          }));
        }
      } catch (error) {
        console.log(error);
        setChecks([]);
      }
    },
    [api, inspection.entity_id, document?.document_id],
  );

  // Initial fetch and reset when dependencies change
  useEffect(() => {
    setCurrentPage(0);
    setCursors({});
    fetchChecks(0);
  }, [fetchChecks]);

  // Polling effect - only updates current page
  useEffect(() => {
    let interval: NodeJS.Timeout;
    const pollCurrentPage = () => {
      const currentCursor = currentPage === 0 ? null : cursors[currentPage];
      fetchChecks(currentPage, currentCursor);
    };

    interval = setInterval(pollCurrentPage, POLL_INTERVAL);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [currentPage, cursors, fetchChecks]);

  const handlePaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      if (model.page === currentPage) return;

      setCurrentPage(model.page);
      const cursor = model.page === 0 ? null : cursors[model.page];
      fetchChecks(model.page, cursor);
    },
    [currentPage, cursors, fetchChecks],
  );

  const renderCompliant = useCallback(
    (params: GridRenderCellParams<InspectionCheckResult>) => {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.value === true ? (
            <CheckCircleIcon color='success' />
          ) : params.value === false ? (
            <HighlightOffIcon color='error' />
          ) : (
            ''
          )}
        </Box>
      );
    },
    [],
  );

  const renderRationale = useCallback((params: GridRenderCellParams<InspectionCheckResult>) => {
    return (
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
        }}
      >
        {(params.value as string | null | undefined) || ''}
      </span>
    );
  }, []);

  const renderSuggestion = useCallback(
    (params: GridRenderCellParams<InspectionCheckResult>) => {
      if (params.row.compliant) {
        return '';
      } else {
        return (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {(params.value as string | null | undefined) || ''}
          </span>
        );
      }
    },
    [],
  );

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    const field = (event.currentTarget as HTMLElement).dataset['field'];
    if (field && ['rationale', 'suggestion'].includes(field)) {
      const id = (event.currentTarget.parentElement as HTMLElement)?.dataset['id'];
      const row = checks.find((r) => r.entity_id === id);
      if (row) {
        setValue((row[field] as string) || '');
        setAnchorEl(event.currentTarget);
      }
    } else {
      setValue('');
    }
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const columns = useMemo<GridColDef<InspectionCheckResult>[]>(
    () => [
      {
        field: 'check_section',
        headerName: 'Section',
        flex: 0.5,
        type: 'string',
      },
      {
        field: 'check_name',
        headerName: 'Name',
        flex: 1,
        type: 'string',
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        type: 'string',
      },
      {
        field: 'compliant',
        headerName: 'Compliant',
        flex: 0.25,
        type: 'boolean',
        renderCell: renderCompliant,
      },
      {
        field: 'rationale',
        headerName: 'Rationale',
        description:
          'The rationale explains how the model determined the compliant status. If compliance is passed, there will be no suggestions shown in this field.',
        flex: 1,
        type: 'string',
        renderCell: renderRationale,
      },
      {
        field: 'suggestion',
        headerName: 'Suggestion',
        description:
          'This field shows recommendations to help you meet the requirement and achieve compliance.',
        flex: 1,
        type: 'string',
        renderCell: renderSuggestion,
      },
    ],
    [renderCompliant, renderRationale, renderSuggestion],
  );

  if (!checks.length) {
    return <LoadingMask />;
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 200px)',
        width: '100%',
      }}
    >
      <DataGrid
            pageSizeOptions={[PAGE_SIZE]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: PAGE_SIZE, page: 0 },
              },
            }}
            columnHeaderHeight={35}
            sx={(theme) => ({
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.grey[100],
              },
            })}
            getRowId={(row) => row.entity_id}
            columns={columns}
            rows={checks}
            showToolbar
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              cell: {
                onMouseEnter: handlePopoverOpen,
                onMouseLeave: handlePopoverClose,
              },
              toolbar: {
                sx: {
                  button: {
                    color: 'black',
                  },
                  svg: {
                    color: 'black',
                  },
                },
                showQuickFilter: true,
              },
            }}
            paginationMode='server'
            rowCount={hasNextPage ? -1 : currentPage * PAGE_SIZE + checks.length}
            paginationModel={{
              pageSize: PAGE_SIZE,
              page: currentPage,
            }}
            onPaginationModelChange={handlePaginationModelChange}
          />
          <Popover
            sx={{
              pointerEvents: 'none',
            }}
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Typography sx={{ p: 1 }}>{value}</Typography>
          </Popover>
      </Box>
  );
}

export default RunningInspectionDocumentChecks;

